import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';

interface PolicyReviewDraftState {
  reviewType: ConsumerPolicyReviewType | null;
  documents: ConsumerPolicyReviewDocument[];
  responseDeadline: string;
  actions: {
    setReviewType: (reviewType: ConsumerPolicyReviewType) => void;
    addDocument: (document: ConsumerPolicyReviewDocument) => void;
    removeDocument: (key: string) => void;
    setResponseDeadline: (responseDeadline: string) => void;
    reset: () => void;
  };
}

const EMPTY_DRAFT = { reviewType: null, documents: [], responseDeadline: '' };

/**
 * The policy review being put together. Kept for the browser session, so a consumer who leaves
 * Stripe checkout without paying comes back to their documents.
 */
export const usePolicyReviewDraftStore = create<PolicyReviewDraftState>()(
  persist(
    set => ({
      ...EMPTY_DRAFT,
      actions: {
        setReviewType: reviewType => set({ reviewType }),
        addDocument: document => set(state => ({ documents: [...state.documents, document] })),
        removeDocument: key =>
          set(state => ({ documents: state.documents.filter(document => document.key !== key) })),
        setResponseDeadline: responseDeadline => set({ responseDeadline }),
        reset: () => set(EMPTY_DRAFT),
      },
    }),
    {
      name: 'consumer-policy-review-draft',
      storage: createJSONStorage(() => sessionStorage),
      partialize: ({ reviewType, documents, responseDeadline }) => ({ reviewType, documents, responseDeadline }),
    },
  ),
);

export function usePolicyReviewDraft() {
  return usePolicyReviewDraftStore(
    useShallow(({ reviewType, documents, responseDeadline }) => ({
      reviewType,
      documents,
      responseDeadline,
    })),
  );
}

export function usePolicyReviewDraftActions() {
  return usePolicyReviewDraftStore(state => state.actions);
}
